import { Injectable, isDevMode } from '@angular/core';

export interface RuntimeConfig {
  buildVersion: string;
  /**
   * `false` turns the dev proxy off, so a dev build calls the tenant's backend
   * directly. On when missing. See the "Dev proxy" section in the README.
   */
  devProxy?: boolean;
  oauth: {
    scopes: string;
    clientId: string;
    redirectUri: string;
  };
  /**
   * Microsoft Graph access for filing several selected mails at once. Batch
   * filing is off for a deployment without it.
   */
  graph?: {
    /** The CMI multi-tenant Entra app. */
    clientId: string;
    /** Defaults to https://login.microsoftonline.com/common. */
    authority?: string;
    /** The manifest `<Id>` of this environment, used for the `cecp-` property name. */
    addinId: string;
  };
}

@Injectable({ providedIn: 'root' })
export class ConfigService {
  private _config: RuntimeConfig | undefined;

  public get config(): RuntimeConfig {
    if (!this._config) {
      throw new Error('Runtime configuration not loaded');
    }
    return this._config;
  }

  /**
   * Whether backend calls go through the dev server proxy: in dev mode unless
   * `config.json` sets `devProxy: false`, never in a production build. See the
   * "Dev proxy" section in the README.
   */
  public get devProxy(): boolean {
    return isDevMode() && this._config?.devProxy !== false;
  }

  public async load(): Promise<void> {
    // Try root-level config.json; fall back to /assets/config.json if not found
    // Angular copies everything from public/ to site root
    const candidates = ['/config.json', '/assets/config.json'];
    for (const url of candidates) {
      try {
        const response = await fetch(url, { cache: 'no-store' });
        if (!response.ok) {
          continue;
        }
        const data = (await response.json()) as RuntimeConfig;
        if (data) {
          this._config = data;
          return;
        }
      } catch {
        // try next
      }
    }
    throw new Error('No runtime configuration file found (config.json)');
  }
}
